import React from 'react';
import { Modal, View, Text, Pressable, StyleSheet, ViewStyle } from 'react-native';
import type { AlertButton } from 'react-native';
import { createBoxShadow } from '../lib/design-tokens';

type CrossPlatformAlertProps = {
  visible: boolean;
  title: string;
  message: string;
  buttons: AlertButton[];
  onClose: () => void;
};

/**
 * A custom alert component for web and a potential fallback for mobile.
 *
 * This component provides a cross-platform alert dialog that works on web
 * where React Native's Alert.alert doesn't function. On iOS/Android, the
 * native Alert.alert is used instead (see AlertProvider).
 */
export const CrossPlatformAlert = ({
  visible,
  title,
  message,
  buttons,
  onClose,
}: CrossPlatformAlertProps) => {
  if (!visible) {
    return null;
  }

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable style={styles.alertBox} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.message}>{message}</Text>
          <View style={styles.buttonContainer}>
            {buttons.map((button, index) => {
              const handlePress = () => {
                if (button.onPress) {
                  button.onPress();
                }
                onClose();
              };

              const isDestructive = button.style === 'destructive';
              const isCancel = button.style === 'cancel';

              return (
                <Pressable
                  key={index}
                  style={[
                    styles.button,
                    isDestructive && styles.destructiveButton,
                    isCancel && styles.cancelButton,
                  ]}
                  onPress={handlePress}
                >
                  <Text
                    style={[
                      styles.buttonText,
                      isDestructive && styles.destructiveButtonText,
                      isCancel && styles.cancelButtonText,
                    ]}
                  >
                    {button.text}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// Platform-aware alert box shadow
const alertBoxShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 2,
  blurRadius: 3.84,
  opacity: 0.25,
  elevation: 24,
});

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  alertBox: {
    width: 320,
    maxWidth: '90%',
    padding: 24,
    backgroundColor: 'white',
    borderRadius: 12,
    ...alertBoxShadow,
  } as ViewStyle,
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#000',
  },
  message: {
    fontSize: 14,
    marginBottom: 24,
    color: '#666',
    lineHeight: 20,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  button: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#007aff',
    minWidth: 80,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  destructiveButton: {
    backgroundColor: '#ff3b30',
  },
  destructiveButtonText: {
    color: 'white',
  },
  cancelButton: {
    backgroundColor: '#f0f0f0',
  },
  cancelButtonText: {
    color: '#000',
  },
});
